// /company-setup/$entity — TH detail page
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import type { CompanyEntity } from "@/data/company-setup-types";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, howToSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { Phone, MessageCircle, ArrowRight, Sparkles, FileText, Wallet, Clock, Users, ChevronRight, ShieldCheck, Building2, Globe } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/company-setup/$entity")({
  loader: async ({ params }) => {
    const { COMPANY_ENTITIES } = await import("@/data/company-setup-types");
    const e = COMPANY_ENTITIES.find((x) => x.slug === params.entity);
    if (!e) throw notFound();
    return {
      __d: { COMPANY_ENTITIES }, entity: e };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const e = loaderData.entity;
    const url = `${SITE}/company-setup/${e.slug}`;
    const title = `${e.titleTh} — เงื่อนไข ขั้นตอน ค่าธรรมเนียม 2026 | NYC`;
    const desc = e.quickAnswerTh.slice(0, 155);
    return {
      meta: [
        { title: ctaTitle(title, "/company-setup/$entity", "th") }, { name: "description", content: ctaDesc(desc, "/company-setup/$entity", "th")},
        { property: "og:title", content: ctaTitle(title, "/company-setup/$entity", "th")}, { property: "og:description", content: ctaDesc(desc, "/company-setup/$entity", "th")},
        { property: "og:url", content: url }, { property: "og:locale", content: "th_TH" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/company-setup/${e.slug}`, `/en/company-setup/${e.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: e.titleTh, serviceType: e.titleEn, description: desc,
              provider: { "@type": "LegalService", name: "NYC Legal & Notary Service", telephone: "+66922429293", url: SITE },
              areaServed: "TH",
              offers: { "@type": "Offer", price: String(e.servicePriceTHB), priceCurrency: "THB", description: `บริการจดทะเบียน ${e.titleTh}` },
            }),
          },
          howToSchema({
            name: `วิธีจดทะเบียน ${e.titleTh}`,
            description: e.taglineTh,
            totalTime: "P30D",
            estimatedCost: { currency: "THB", value: String(e.servicePriceTHB) },
            inLanguage: "th",
            steps: e.stepsTh,
          }),
          faqPageSchema(e.faqsTh),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
                { "@type": "ListItem", position: 2, name: "จดทะเบียนบริษัท", item: `${SITE}/company-setup` },
                { "@type": "ListItem", position: 3, name: e.titleTh, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="text-3xl font-extrabold">ไม่พบรูปแบบนี้</h1>
      <Link to="/company-setup" className="mt-6 inline-block rounded-lg bg-primary px-5 py-3 font-bold text-primary-foreground">กลับไปหน้าจดทะเบียนบริษัท</Link>
    </main>
  ),
  errorComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="text-2xl font-bold">เกิดข้อผิดพลาด</h1>
      <Link to="/company-setup" className="mt-4 inline-block text-primary">กลับไปหน้าจดทะเบียนบริษัท</Link>
    </main>
  ),
  component: CompanyEntityTh,
});

function CompanyEntityTh() {
  const { __d: { COMPANY_ENTITIES } } = Route.useLoaderData() as { __d: typeof import("@/data/company-setup-types") };
  const { entity: e } = Route.useLoaderData() as { entity: CompanyEntity };
  const others = COMPANY_ENTITIES.filter((x) => x.slug !== e.slug);
  return (
    <main className="min-h-screen bg-background">
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-16 pb-12">
        <div className="mx-auto max-w-5xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary">หน้าแรก</Link>
            <ChevronRight className="mx-1 inline h-3 w-3" />
            <Link to="/company-setup" className="hover:text-primary">จดทะเบียนบริษัท</Link>
            <ChevronRight className="mx-1 inline h-3 w-3" />
            <span>{e.titleTh}</span>
          </nav>
          <div className="text-4xl">{e.icon}</div>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">{e.titleTh}</h1>
          <p className="mt-3 max-w-3xl text-lg text-muted-foreground">{e.taglineTh}</p>

          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <p className="text-sm leading-relaxed text-foreground sm:text-base">{e.quickAnswerTh}</p>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-3 max-w-3xl">
            <div className="rounded-xl border border-border bg-card p-3">
              <Wallet className="h-4 w-4 text-primary" />
              <div className="mt-1 text-xs text-muted-foreground">ทุนจดทะเบียน</div>
              <div className="text-xs font-bold line-clamp-2">{e.capitalTh}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-3">
              <Globe className="h-4 w-4 text-primary" />
              <div className="mt-1 text-xs text-muted-foreground">ต่างชาติถือหุ้น</div>
              <div className="text-xs font-bold line-clamp-2">{e.foreignOwnershipTh}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-3">
              <Clock className="h-4 w-4 text-primary" />
              <div className="mt-1 text-xs text-muted-foreground">เวลาดำเนินการ</div>
              <div className="text-sm font-bold">{e.setupDays}</div>
            </div>
          </div>

          <div className="mt-7 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> โทร 092-242-9293
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> LINE @nyc-notary
            </a>
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto grid max-w-5xl gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h2 className="text-2xl font-extrabold">ขั้นตอนจดทะเบียน</h2>
              <ol className="mt-4 space-y-3">
                {e.stepsTh.map((step, i) => (
                  <li key={i} className="rounded-xl border border-border bg-card p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">{i + 1}</div>
                      <div>
                        <div className="font-bold">{step.name}</div>
                        <p className="mt-1 text-sm text-muted-foreground">{step.text}</p>
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div>
              <h2 className="text-2xl font-extrabold">เอกสารที่ต้องเตรียม</h2>
              <ul className="mt-4 space-y-2">
                {e.docsTh.map((d, i) => (
                  <li key={i} className="flex items-start gap-2 rounded-lg border border-border bg-card p-3 text-sm">
                    <FileText className="h-4 w-4 shrink-0 text-primary" />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-extrabold">คำถามที่พบบ่อย</h2>
              <div className="mt-4 space-y-3">
                {e.faqsTh.map((f, i) => (
                  <details key={i} className="rounded-xl border border-border bg-card p-4">
                    <summary className="cursor-pointer font-bold">{f.q}</summary>
                    <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>

          <aside className="space-y-4">
            <div className="rounded-2xl border border-border bg-card p-5">
              <h3 className="font-bold flex items-center gap-1.5"><Users className="h-4 w-4 text-primary" /> เหมาะกับใคร</h3>
              <p className="mt-2 text-sm text-muted-foreground">{e.bestForTh}</p>
            </div>
            <div className="rounded-2xl border border-primary/40 bg-primary/5 p-5">
              <h3 className="font-bold flex items-center gap-1.5"><Building2 className="h-4 w-4 text-primary" /> ภาษีและสิทธิ</h3>
              <p className="mt-2 text-sm">{e.taxNoteTh}</p>
            </div>
            <div className="rounded-2xl border border-primary bg-primary/5 p-5">
              <h3 className="font-bold">บริการของ NYC</h3>
              <p className="mt-2 text-2xl font-extrabold text-primary">฿{e.servicePriceTHB.toLocaleString()}</p>
              <p className="mt-1 text-xs text-muted-foreground">ไม่รวมค่าธรรมเนียมราชการ — รวมการเตรียมเอกสาร ยื่นจดทะเบียน ติดตามผล และให้คำปรึกษาตลอดกระบวนการ</p>
              <a href="tel:+66922429293" className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-primary-foreground">
                <Phone className="h-4 w-4" /> โทรปรึกษา
              </a>
            </div>
            <div className="rounded-2xl border border-border bg-card p-5">
              <h3 className="font-bold">บริการเสริม</h3>
              <ul className="mt-2 space-y-1 text-sm">
                <li><Link to="/work-permit" className="text-primary hover:underline">Work Permit + Non-B Visa</Link></li>
                <li><Link to="/translate" className="text-primary hover:underline">แปลเอกสารบริษัท (MOA, FS)</Link></li>
                <li><Link to="/apostille" className="text-primary hover:underline">Apostille เอกสารบริษัทแม่</Link></li>
                <li><Link to="/visa-compliance" className="text-primary hover:underline">TM30 + 90-day report</Link></li>
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <section className="bg-muted/30 px-4 py-10">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-xl font-extrabold"><ShieldCheck className="mr-2 inline h-5 w-5 text-primary" />รูปแบบนิติบุคคลอื่น</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {others.map((o) => (
              <Link key={o.slug} to="/company-setup/$entity" params={{ entity: o.slug }} className="rounded-xl border border-border bg-card p-3 hover:border-primary">
                <div className="flex items-center gap-2"><span className="text-xl">{o.icon}</span><span className="font-bold text-sm">{o.titleTh}</span></div>
                <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">{o.taglineTh}</div>
                <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary">ดู <ArrowRight className="h-3 w-3" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <TrustLayer variant="compact" />
    </main>
  );
}
